import React, { useState } from "react";
import { Link } from "react-router-dom";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import CustomerOnlinePaymentStatus from "../../components/CustomerOnlinePaymentStatus";
import { buildProductDetailPath } from "../../utils/productLinks";
import { getProductDisplayName } from "../../utils/productDisplayName";
import {
  getDeliveryMethodLabel,
  isBranchDelivery,
  isKyivCourierDelivery,
  isNovaPoshtaCourierDelivery,
} from "../../api/checkoutDeliverySettingsApi";
import { isOrderFreeDelivery } from "../../utils/freeDelivery";
import styles from "./OrdersSectionMobile.module.css";

// === Мапа доставки ===
const DELIVERY_METHODS_MAP = {
  branch: "У відділення Нової пошти",
  locker: "У поштомат Нової пошти",
  courier: "Курʼєром",
  "courier-nova-poshta": "Курʼєром Нової пошти",
  "courier-kyiv": "Курʼєром по Києву",
  pickup: "Самовивіз",
};

// === Текст для вартості доставки ===
function getDeliveryCostText(order) {
  if (isOrderFreeDelivery(order)) return "безкоштовно";
  return "за тарифами перевізника";
}

const parseBranch = (branchString) => {
  if (!branchString) return { number: "", weightLimit: "", address: "" };
  const matchNumber = branchString.match(/№\d+/);
  const number = matchNumber ? matchNumber[0] : "";
  const matchWeight = branchString.match(/\(([^)]+)\)/);
  const weightLimit = matchWeight ? matchWeight[1] : "";
  const address = branchString.split(":")[1]?.trim() || "";
  return { number, weightLimit, address };
};

const getPaymentDetails = (order = {}) => order.paymentDetails || {};

const getPaymentPurpose = (order = {}) => {
  const details = getPaymentDetails(order);
  return (
    details.paymentPurpose ||
    `${details.purposePrefix || "Оплата замовлення"} №${order.orderNumber || "—"}`
  );
};

const getProductImageSrc = (item = {}) => {
  const imageCandidate =
    item.image ||
    item.imageUrl ||
    item.productImage ||
    item.mainImage ||
    item.product?.image ||
    item.product?.imageUrl ||
    item.images?.[0] ||
    item.product?.images?.[0];

  if (typeof imageCandidate === "string" && imageCandidate.trim()) {
    return imageCandidate;
  }

  if (imageCandidate?.url) return imageCandidate.url;
  if (imageCandidate?.src) return imageCandidate.src;

  return "/no-image.png";
};

const OrdersSectionMobile = ({ orders }) => {
  const [openDetails, setOpenDetails] = useState(null);

  const toggleDetails = (orderId) => {
    setOpenDetails(openDetails === orderId ? null : orderId);
  };

  if (!orders?.length)
    return <div className={styles.emptyMsg}>У вас ще немає замовлень.</div>;

  return (
    <div className={styles.ordersListMobile}>
      {orders.map((order) => {
        const cartItems = Array.isArray(order.cartItems)
          ? order.cartItems
          : [];
        const { number, weightLimit, address } = parseBranch(
          order.branchNumber
        );
        const paymentDetails = getPaymentDetails(order);
        // --- Формування блоку Інформація про доставку ---
        const deliveryInfoBlock = (
          <div className={styles.deliverySection}>
            <div className={styles.sectionTitleBorder}>
              Інформація про доставку
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Спосіб доставки:</span>
              <span className={styles.infoValue}>
                {DELIVERY_METHODS_MAP[order.deliveryMethod] ||
                  getDeliveryMethodLabel(order.deliveryMethod) ||
                  order.deliveryMethod}
              </span>
            </div>
            {isBranchDelivery(order.deliveryMethod) && (
              <>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Місто:</span>
                  <span className={styles.infoValue}>{order.city || "—"}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Відділення НП:</span>
                  <span className={styles.infoValue}>
                    {number || "-"}
                    {weightLimit ? ` (${weightLimit})` : ""}
                  </span>
                </div>
                {address && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Адреса відділення:</span>
                    <span className={styles.infoValue}>{address}</span>
                  </div>
                )}
              </>
            )}
            {isNovaPoshtaCourierDelivery(order.deliveryMethod) && (
              <>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Місто:</span>
                  <span className={styles.infoValue}>{order.city || "—"}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Адреса:</span>
                  <span className={styles.infoValue}>
                    вул. {order.street || "—"}, буд.{" "}
                    {order.buildingNumber || "—"}
                    {order.entranceNumber
                      ? `, під'їзд ${order.entranceNumber}`
                      : ""}
                  </span>
                </div>
                {order.courierNote && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>
                      Примітка для кур’єра:
                    </span>
                    <span className={styles.infoValue}>
                      {order.courierNote}
                    </span>
                  </div>
                )}
              </>
            )}
            {isKyivCourierDelivery(order.deliveryMethod) && (
              <>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Адреса:</span>
                  <span className={styles.infoValue}>
                    вул. {order.street || "—"}, буд.{" "}
                    {order.buildingNumber || "—"}
                    {order.entranceNumber
                      ? `, під'їзд ${order.entranceNumber}`
                      : ""}
                  </span>
                </div>
                {order.courierNote && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>
                      Примітка для кур’єра:
                    </span>
                    <span className={styles.infoValue}>
                      {order.courierNote}
                    </span>
                  </div>
                )}
                {/* Додаткові службові поля */}
                {order.deliveryService && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Сервіс доставки:</span>
                    <span className={styles.infoValue}>
                      {order.deliveryService}
                    </span>
                  </div>
                )}
                {order.car && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Автомобіль:</span>
                    <span className={styles.infoValue}>{order.car}</span>
                  </div>
                )}
                {order.driver && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Водій:</span>
                    <span className={styles.infoValue}>{order.driver}</span>
                  </div>
                )}
                {order.driverPhone && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Телефон водія:</span>
                    <span className={styles.infoValue}>
                      {order.driverPhone}
                    </span>
                  </div>
                )}
              </>
            )}
            {order.ttn && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>ТТН:</span>
                <span className={styles.infoValue}>{order.ttn}</span>
              </div>
            )}
            {/* Вартість доставки — останнє поле */}
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Вартість доставки:</span>
              <span className={styles.infoValue}>
                {getDeliveryCostText(order)}
              </span>
            </div>
          </div>
        );

        // --- Формування блоку Інформація про клієнта ---
        const clientInfoBlock = (
          <div className={styles.clientSection}>
            <div className={styles.sectionTitleBorder}>
              Інформація про клієнта
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Клієнт:</span>
              <span className={styles.infoValue}>{order.customerName}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Телефон:</span>
              <span className={styles.infoValue}>{order.phone}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Сума замовлення:</span>
              <span className={styles.infoValue}>{order.totalAmount} ₴</span>
            </div>
            {/* Останнім полем — спосіб оплати */}
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Спосіб оплати:</span>
              <span className={styles.infoValue}>{order.paymentMethod}</span>
            </div>
          </div>
        );

        return (
          <div className={styles.orderCardMobile} key={order._id}>
            <div className={styles.orderCardHeader}>
              <div className={styles.orderHeaderMain}>
                <div className={styles.orderHeaderMeta}>
                  <span className={styles.orderNum}>
                    <span className={styles.orderNumLabel}>Замовлення</span>
                    <span>№ {order.orderNumber}</span>
                  </span>
                  <span className={styles.orderDate}>
                    {new Date(order.createdAt).toLocaleString("uk-UA")}
                  </span>
                  <span
                    className={`${styles.orderStatus} ${styles[order.status]}`}
                  >
                    {order.status}
                  </span>
                </div>
                <div className={styles.mobilePreviewImages}>
                  {cartItems.map((item, i) => (
                    <img
                      key={item.productId || i}
                      src={getProductImageSrc(item)}
                      alt={item.name}
                      className={styles.mobilePreviewImage}
                      loading="lazy"
                      decoding="async"
                    />
                  ))}
                </div>
              </div>

              <button
                className={styles.orderExpandBtn}
                onClick={() => toggleDetails(order._id)}
                type="button"
                aria-expanded={openDetails === order._id}
                aria-label={
                  openDetails === order._id
                    ? "Згорнути деталі замовлення"
                    : "Розкрити деталі замовлення"
                }
              >
                {openDetails === order._id ? <IoChevronUp /> : <IoChevronDown />}
              </button>
            </div>

            {openDetails === order._id && (
              <div className={styles.orderDetailsMobile}>
                {clientInfoBlock}
                {deliveryInfoBlock}
                <CustomerOnlinePaymentStatus order={order} />
                {order.paymentMethod === "Оплата за реквізитами" && (
                  <div className={styles.paymentRequisitesSectionMobile}>
                    <div className={styles.sectionTitleBorder}>
                      Реквізити для оплати
                    </div>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>Отримувач:</span>
                      <span className={styles.infoValue}>
                        {paymentDetails.recipientName || "—"}
                      </span>
                    </div>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>IBAN:</span>
                      <span className={styles.infoValue}>
                        {paymentDetails.iban || "—"}
                      </span>
                    </div>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>РНОКПП / ЄДРПОУ:</span>
                      <span className={styles.infoValue}>
                        {paymentDetails.edrpou || "—"}
                      </span>
                    </div>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>Банк:</span>
                      <span className={styles.infoValue}>
                        {paymentDetails.bankName || "—"}
                      </span>
                    </div>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>Сума до оплати:</span>
                      <span className={styles.infoValue}>
                        {paymentDetails.amount || order.totalAmount || 0} ₴
                      </span>
                    </div>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>
                        Призначення платежу:
                      </span>
                      <span className={styles.infoValue}>
                        {getPaymentPurpose(order)}
                      </span>
                    </div>
                    <div className={styles.requisitesNoticeMobile}>
                      Відправлення замовлення здійснюється після надходження{" "}
                      <strong>100% оплати</strong> на рахунок продавця.
                    </div>
                  </div>
                )}
                <div className={styles.productsSection}>
                  <div className={styles.sectionTitleBorder}>
                    Придбані товари
                  </div>
                  {cartItems.map((item, idx) => (
                    <div
                      className={styles.orderProductRow}
                      key={item.productId || idx}
                    >
                      <img
                        src={getProductImageSrc(item)}
                        alt={item.name}
                        className={styles.productImage}
                        loading="lazy"
                        decoding="async"
                      />
                      <div className={styles.prodMain}>
                        {item.slug ? (
                          <Link
                            to={buildProductDetailPath({
                              ...item,
                              variantId:
                                item.variantId || item.productId || item._id,
                            })}
                            className={styles.prodName}
                          >
                            {getProductDisplayName(item) || "Товар"}
                          </Link>
                        ) : (
                          <span className={styles.prodName}>
                            {getProductDisplayName(item) || "Товар"}
                          </span>
                        )}
                        <div className={styles.prodLine}>
                          <span className={styles.prodInfo}>
                            Артикул: {item.productCode} | {item.quantity} ×{" "}
                            {item.price} ₴
                          </span>
                          <span className={styles.prodTotal}>
                            Підсумок: {item.quantity * item.price} ₴
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {order.comment && (
                  <div className={styles.orderComment}>
                    <b>Коментар:</b> {order.comment}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default OrdersSectionMobile;
